import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import {
  Box, Container, Typography, Button, TextField, CircularProgress,
  Snackbar, Alert, Paper, Divider, InputAdornment, Switch, FormControlLabel,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PlaceIcon from '@mui/icons-material/Place';
import { Map, Marker } from '@vis.gl/react-maplibre';
import { useTranslation } from 'react-i18next';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

const fieldSx = (focusColor = AMBER) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: '"Outfit", sans-serif',
    borderRadius: '10px',
    '&.Mui-focused fieldset': { borderColor: focusColor },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: focusColor },
});

const SchoolPage = () => {
  const { authTokens, backendUrl, pageTitle } = useContext(AuthContext);
  const { t } = useTranslation();
  document.title = (pageTitle || 'App') + ' - ' + t('school.title');

  const [form, setForm] = useState({
    name: '', address: '', email: '', web_site: '',
    mobile_number: '', tel_number: '', latitude: '', longitude: '',
    enable_route_replay: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', text: '' });

  const authHeader = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + String(authTokens.access),
  }), [authTokens]);

  useEffect(() => {
    fetch(backendUrl + 'api/school/', { headers: authHeader() })
      .then((r) => r.json())
      .then((data) => { setForm(data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  const phoneFields = ['tel_number', 'mobile_number'];
  const handleChange = (field) => (e) => {
    const value = phoneFields.includes(field) ? e.target.value.replace(/[^0-9+\- ]/g, '') : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleMapClick = (e) => {
    const { lng, lat } = e.lngLat;
    setForm((f) => ({ ...f, longitude: String(lng.toFixed(7)), latitude: String(lat.toFixed(7)) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(backendUrl + 'api/school/', {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setForm(data);
      setToast({ open: true, severity: 'success', text: t('school.saved') });
    } catch {
      setToast({ open: true, severity: 'error', text: t('school.save_failed') });
    } finally {
      setSaving(false);
    }
  };

  const lng = parseFloat(form.longitude) || 0;
  const lat = parseFloat(form.latitude) || 0;
  const hasLocation = form.longitude && form.latitude && lng !== 0 && lat !== 0;

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 8 }}>
      <Container maxWidth="lg">

        {/* Page header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: '12px', background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`, boxShadow: '0 4px 14px rgba(15,45,94,0.25)', flexShrink: 0 }}>
              <SchoolIcon sx={{ color: AMBER, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: NAV_BG, lineHeight: 1.2 }}>
                {t('school.title')}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: 0.1 }}>
                {t('school.subtitle')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: '10px', px: 2.5, py: 1, background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, boxShadow: '0 6px 20px rgba(245,158,11,0.35)' }, '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' } }}
          >
            {saving ? t('school.saving') : t('school.save_changes')}
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

          {/* Left: school details */}
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', p: 3, boxShadow: '0 2px 16px rgba(15,45,94,0.06)' }}>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: NAV_BG, mb: 2.5 }}>
              {t('school.general_info')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField label={t('school.school_name')} value={form.name} onChange={handleChange('name')} fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><SchoolIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={fieldSx()} />
              <TextField label={t('school.address')} value={form.address} onChange={handleChange('address')} fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><HomeIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={fieldSx()} />
              <TextField label={t('school.email')} value={form.email} onChange={handleChange('email')} fullWidth size="small" type="email"
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={fieldSx()} />
              <TextField label={t('school.website')} value={form.web_site} onChange={handleChange('web_site')} fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><LanguageIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={fieldSx()} />
              <Divider />
              <TextField label={t('school.phone')} value={form.tel_number} onChange={handleChange('tel_number')} fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={fieldSx()} />
              <TextField label={t('school.mobile')} value={form.mobile_number} onChange={handleChange('mobile_number')} fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><SmartphoneIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={fieldSx()} />
            </Box>
          </Paper>

          {/* Right: location */}
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', p: 3, boxShadow: '0 2px 16px rgba(15,45,94,0.06)' }}>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: NAV_BG, mb: 0.5 }}>
              {t('school.location')}
            </Typography>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', mb: 2 }}>
              {t('school.location_hint')}
            </Typography>
            <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', mb: 2, height: 280 }}>
              <Map
                initialViewState={{ longitude: hasLocation ? lng : 23, latitude: hasLocation ? lat : 38, zoom: hasLocation ? 14 : 5 }}
                style={{ width: '100%', height: '100%' }}
                mapStyle={MAP_STYLE}
                onClick={handleMapClick}
                cursor="crosshair"
              >
                {hasLocation && (
                  <Marker longitude={lng} latitude={lat} anchor="bottom">
                    <PlaceIcon sx={{ fontSize: 36, color: AMBER_DARK, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
                  </Marker>
                )}
              </Map>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('school.latitude')} value={form.latitude} onChange={handleChange('latitude')} fullWidth size="small" type="number" sx={fieldSx()} />
              <TextField label={t('school.longitude')} value={form.longitude} onChange={handleChange('longitude')} fullWidth size="small" type="number" sx={fieldSx()} />
            </Box>
          </Paper>
        </Box>

        {/* Route Replay */}
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', p: 3, mt: 3, boxShadow: '0 2px 16px rgba(15,45,94,0.06)' }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: NAV_BG, mb: 0.5 }}>
            {t('school.route_replay_section')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', mb: 2 }}>
            {t('school.route_replay_hint')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.enable_route_replay}
                onChange={(e) => setForm((f) => ({ ...f, enable_route_replay: e.target.checked }))}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER },
                }}
              />
            }
            label={
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: NAV_BG, fontWeight: 500 }}>
                {t('school.route_replay_label')}
              </Typography>
            }
          />
        </Paper>

      </Container>

      <Snackbar open={toast.open} autoHideDuration={20000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ fontFamily: '"Outfit", sans-serif' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolPage;
